import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { PricingTableFormValues } from "@/schemas/pricingTableSchema";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Trash2, Plus, Users, HeartHandshake, Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocumentType } from '@/models';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PricingTableRegularFeesProps {
  feesApportionParties: any[];
  documentTypes: DocumentType[];
}

export const PricingTableRegularFees: React.FC<PricingTableRegularFeesProps> = ({
  feesApportionParties,
  documentTypes
}) => {
  const { control, watch, setValue } = useFormContext<PricingTableFormValues>();
  
  const {
    fields: documentFeeFields,
    append: appendDocumentFee,
    remove: removeDocumentFee,
    update: updateDocumentFee
  } = useFieldArray({
    control,
    name: "document_fees"
  });

  const handleAddFeeToDocType = (docTypeId: number) => {
    const existingDocTypeIndex = documentFeeFields.findIndex(
      df => df.document_type_id === docTypeId
    );
    
    const docTypeName = documentTypes.find(dt => dt.id === docTypeId)?.name || "";
    
    if (existingDocTypeIndex >= 0) {
      // Get the existing document fee entry
      const docType = documentFeeFields[existingDocTypeIndex];
      
      // Generate a new fee ID
      const newFeeId = docType.fees.length > 0 
        ? Math.max(...docType.fees.map(fee => Number(fee.fee_id) || 0)) + 1 
        : 1;
      
      // Create default amounts for apportionment
      const defaultAmount = 0;
      const defaultApportions = feesApportionParties.map(party => ({
        apportionment_party_id: party.id,
        amount: defaultAmount
      }));
      
      // Create a new fee object
      const newFee = {
        fee_id: newFeeId,
        name: `Document Fee`,
        multiply_by_applicants: false,
        married_count_as_one: false,
        amount: 0,
        timing: 'upfront' as const,
        apportionment: defaultApportions
      };
      
      // Add the new fee to the existing fees array
      const updatedFees = [...docType.fees, newFee];
      
      // Update the document fee entry with the updated fees array
      updateDocumentFee(existingDocTypeIndex, {
        ...docType,
        fees: updatedFees
      });
    } else {
      // Create a new document fee entry
      const defaultAmount = 0;
      const defaultApportions = feesApportionParties.map(party => ({
        apportionment_party_id: party.id,
        amount: defaultAmount
      }));
      
      appendDocumentFee({
        document_type_id: docTypeId,
        document_type_name: docTypeName,
        fees: [
          {
            fee_id: 1,
            name: `Document Fee`,
            multiply_by_applicants: false,
            married_count_as_one: false,
            amount: 0,
            timing: 'upfront' as const,
            apportionment: defaultApportions
          }
        ]
      });
    }
  };
  
  const updateApportionmentAmount = (docFeeIndex: number, feeIndex: number, partyId: number, value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value);
    
    const currentApportionments = [...(watch(`document_fees.${docFeeIndex}.fees.${feeIndex}.apportionment`) || [])];
    
    const apportionmentIndex = currentApportionments.findIndex(a => 
      a.apportionment_party_id === partyId
    );
    
    if (apportionmentIndex >= 0) {
      const updatedApportionments = [...currentApportionments];
      updatedApportionments[apportionmentIndex] = {
        ...updatedApportionments[apportionmentIndex],
        amount: numValue
      };
      setValue(`document_fees.${docFeeIndex}.fees.${feeIndex}.apportionment`, updatedApportionments);
    } else {
      const newApportionment = [
        ...currentApportionments, 
        {
          apportionment_party_id: partyId,
          amount: numValue
        }
      ];
      setValue(`document_fees.${docFeeIndex}.fees.${feeIndex}.apportionment`, newApportionment);
    }
    
    setTimeout(() => {
      validateRowTotal(docFeeIndex, feeIndex);
    }, 100);
  };

  const validateRowTotal = (docFeeIndex: number, feeIndex: number) => {
    const apportionments = watch(`document_fees.${docFeeIndex}.fees.${feeIndex}.apportionment`) || [];
    const feeAmount = watch(`document_fees.${docFeeIndex}.fees.${feeIndex}.amount`) || 0;
    const total = apportionments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    
    if (Math.abs(total - feeAmount) > 0.01) {
      toast.warning(`Fee apportionments total should equal the fee amount of $${feeAmount.toFixed(2)}`);
    }
  };

  const getApportionmentAmount = (apportionments: any[] = [], partyId: number) => {
    const apportionment = apportionments.find(a => a.apportionment_party_id === partyId);
    return apportionment ? apportionment.amount || 0 : 0;
  };
  
  const handleRemoveFee = (docIndex: number, feeIndex: number) => {
    const currentDocFee = documentFeeFields[docIndex];
    const currentFees = [...currentDocFee.fees];
    currentFees.splice(feeIndex, 1);
    
    if (currentFees.length === 0) {
      removeDocumentFee(docIndex);
    } else {
      updateDocumentFee(docIndex, {
        ...currentDocFee,
        fees: currentFees
      });
    }
  };
  
  const getDocTypeConfig = (docTypeId: number) => {
    return documentFeeFields.find(df => df.document_type_id === docTypeId);
  };

  return (
    <TooltipProvider>
      <div className="rounded-md border shadow-sm bg-white">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Regular Fees</h3>
          <p className="text-sm text-muted-foreground">Configure fees for individual document types</p>
        </div>
        <div className="p-4">
          <Accordion type="multiple" className="w-full">
            {documentTypes.map((docType) => {
              const docFeeConfig = getDocTypeConfig(docType.id);
              const docFeeIndex = documentFeeFields.findIndex(df => df.document_type_id === docType.id);
              
              return (
                <AccordionItem key={docType.id} value={`doc-${docType.id}`}>
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-8">
                      <span className="font-medium">{docType.name}</span>
                      {docFeeConfig ? (
                        <span className="text-gray-500 text-sm">
                          ({docFeeConfig.fees.length} {docFeeConfig.fees.length === 1 ? 'fee' : 'fees'})
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">No fees defined</span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pt-2">
                    <div className="flex justify-end mb-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddFeeToDocType(docType.id)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Fee
                      </Button>
                    </div>
                    
                    {docFeeConfig && docFeeConfig.fees.length > 0 ? (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Fee Name</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead className="w-32">Timing</TableHead>
                              <TableHead className="w-24 text-center">Options</TableHead>
                              {feesApportionParties.map(party => (
                                <TableHead key={party.id}>
                                  {party.description || party.entity_name}
                                </TableHead>
                              ))}
                              <TableHead className="w-16">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {docFeeConfig.fees.map((feeField, feeIndex) => (
                              <TableRow key={`fee-${feeField.fee_id}`}>
                                <TableCell>
                                  <FormField
                                    control={control}
                                    name={`document_fees.${docFeeIndex}.fees.${feeIndex}.name`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormControl>
                                          <Input 
                                            className="h-8"
                                            placeholder="Fee name"
                                            {...field}
                                          />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </TableCell>
                                
                                <TableCell>
                                  <FormField
                                    control={control}
                                    name={`document_fees.${docFeeIndex}.fees.${feeIndex}.amount`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormControl>
                                          <div className="flex items-center justify-center space-x-1">
                                            <span className="text-gray-500">$</span>
                                            <Input 
                                              type="number"
                                              min="0"
                                              placeholder="0.00"
                                              className="amount-input"
                                              style={{ appearance: "textfield" }}
                                              value={field.value === undefined || field.value === null ? 0 : field.value}
                                              onChange={(e) => {
                                                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                field.onChange(isNaN(value) ? 0 : value);
                                              }}
                                            />
                                          </div>
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </TableCell>
                                
                                <TableCell>
                                  <FormField
                                    control={control}
                                    name={`document_fees.${docFeeIndex}.fees.${feeIndex}.timing`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormControl>
                                          <Select
                                            value={field.value || 'upfront'}
                                            onValueChange={field.onChange}
                                          >
                                            <SelectTrigger className="h-8">
                                              <SelectValue placeholder="Select timing">
                                                <div className="flex items-center gap-2">
                                                  {field.value === 'at_closing' ? (
                                                    <>
                                                      <Clock className="h-3 w-3 text-orange-500" />
                                                      <span>At Closing</span>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <DollarSign className="h-3 w-3 text-green-500" />
                                                      <span>Upfront</span>
                                                    </>
                                                  )}
                                                </div>
                                              </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="upfront">
                                                <div className="flex items-center gap-2">
                                                  <DollarSign className="h-3 w-3 text-green-500" />
                                                  <span>Upfront</span>
                                                </div>
                                              </SelectItem>
                                              <SelectItem value="at_closing">
                                                <div className="flex items-center gap-2">
                                                  <Clock className="h-3 w-3 text-orange-500" />
                                                  <span>At Closing</span>
                                                </div>
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </TableCell>
                                
                                <TableCell>
                                  <div className="flex items-center justify-center space-x-2">
                                    <FormField
                                      control={control}
                                      name={`document_fees.${docFeeIndex}.fees.${feeIndex}.multiply_by_applicants`}
                                      render={({ field }) => (
                                        <FormItem className="flex flex-col items-center space-y-1">
                                          <FormControl>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <div className="flex flex-col items-center">
                                                  <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                  />
                                                  <Users 
                                                    size={14} 
                                                    className={field.value ? "text-blue-500" : "text-gray-300"} 
                                                  />
                                                </div>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>Multiply by Applicants</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </FormControl>
                                        </FormItem>
                                      )}
                                    />
                                    
                                    <FormField
                                      control={control}
                                      name={`document_fees.${docFeeIndex}.fees.${feeIndex}.married_count_as_one`}
                                      render={({ field }) => (
                                        <FormItem className="flex flex-col items-center space-y-1">
                                          <FormControl>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <div className="flex flex-col items-center">
                                                  <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                  />
                                                  <HeartHandshake
                                                    size={14} 
                                                    className={field.value ? "text-purple-500" : "text-gray-300"} 
                                                  />
                                                </div>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>Married Count as One</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </FormControl>
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                </TableCell>
                                
                                {feesApportionParties.map(party => (
                                  <TableCell key={party.id}>
                                    <div className="flex items-center justify-center space-x-1">
                                      <span className="text-gray-500">$</span>
                                      <Input
                                        type="number"
                                        min="0"
                                        className="apportion-input"
                                        style={{ appearance: "textfield" }}
                                        value={getApportionmentAmount(watch(`document_fees.${docFeeIndex}.fees.${feeIndex}.apportionment`), party.id)}
                                        onChange={(e) => updateApportionmentAmount(docFeeIndex, feeIndex, party.id, e.target.value)}
                                      />
                                    </div>
                                  </TableCell>
                                ))}
                                
                                <TableCell>
                                  <div className="flex space-x-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRemoveFee(docFeeIndex, feeIndex)}
                                      className="text-red-500 h-8 w-8 p-0"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      <span className="sr-only">Remove fee</span>
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500 border rounded-md">
                        No fees defined for this document type
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </div>
    </TooltipProvider>
  );
}; 